/**
 * AP-37 Detailblatt O 7.2 — **eine Anfrage als Seitenfeld rechts**: Kopf der
 * Antwort (Antwort vom, Angebotsnummer, gültig bis, Bemerkung), das Angebot des
 * Lieferanten als Datei, darunter je Zeile EP · PE · Rabatt · Lieferzeit ·
 * nicht angeboten · Alternative · Bemerkung.
 *
 * Gespeichert wird feldweise ohne Knopf (AP-36): eine Speicherstelle für den Kopf,
 * eine je Zeile — jede mit ihrem eigenen Stand. Preise nur mit `cost.view`; ohne
 * das Recht ist das Seitenfeld nur lesbar (Rückweg-Falle).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, FileText, Loader2, Lock, Paperclip, RotateCcw, Sparkles, Trash2, Upload, X } from 'lucide-react'
import { KiArbeitet } from '@/components/ki/KiArbeitet'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { FeldStatusZeichen, feldKlasse, mitStand, useFeld, useSpeicherstelle, useStellenDaten, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import {
  closeRequest,
  deleteRequestFile,
  fetchRequest,
  oeffneAngebotsdatei,
  reopenRequest,
  requestLineUrl,
  requestUrl,
  uploadRequestFile,
  antwortLesen,
  anfrageKiBestaetigen,
  anfrageKiLesen,
  anfrageKiVerwerfen,
} from '../../api/calculation-requests.api'
import type { AnfrageStatus, JeKg, KiAntwortVorschlag, MailImport, RequestDetail, RequestLine } from '../../types/calculation-requests.types'

export const STATUS_ANFRAGE: Record<AnfrageStatus, { label: string; klasse: string }> = {
  DRAFT: { label: 'im Entwurf', klasse: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  REQUESTED: { label: 'angefragt', klasse: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200' },
  ANSWERED: { label: 'beantwortet', klasse: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  CLOSED: { label: 'abgeschlossen', klasse: 'bg-muted text-muted-foreground' },
  CANCELLED: { label: 'storniert', klasse: 'bg-muted text-muted-foreground line-through' },
}

const tag = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '')

/** Was aus einer abgelegten Mail geworden ist — in einem Satz, übersprungene Anhänge mit Grund. */
function mailHinweis(m: MailImport): string {
  const zahl = m.attachments.length
  const teil = zahl === 0 ? 'ohne Anhänge' : zahl === 1 ? 'und 1 Anhang' : `und ${zahl} Anhänge`
  const weg = m.skipped.map((s) => `„${s.fileName}" (${s.reason})`).join(', ')
  return `E-Mail als „${m.fileName}" ${teil} abgelegt.${weg ? ` Nicht abgelegt: ${weg}.` : ''}`
}
const mengeText = (x: number) => x.toLocaleString('de-DE', { maximumFractionDigits: 3 })

/** 7.4a — warum „je kg" hier nicht geht, als Satz am ausgegrauten Feld. */
function ohneJeKg(lage: JeKg): string | null {
  switch (lage.reason) {
    case 'NO_KG_UNIT':
      return 'je kg geht nicht: Der Betrieb hat keine Einheit „kg" angelegt (Artikel → Stammlisten).'
    case 'WEIGHT_MISSING':
      return `je kg geht nicht: Gewicht je Einheit fehlt bei ${lage.missingWeight.join(', ')}.`
    default:
      return null
  }
}
const eingabe = `${feldKlasse} w-full`
const SICHER_KLASSE: Record<KiAntwortVorschlag['confidence'], string> = {
  hoch: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  mittel: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  niedrig: 'bg-amber-500/20 text-amber-800 dark:text-amber-300',
}

export function AnfrageSeitenfeld({ id, onClose, onGeaendert }: { id: string; onClose: () => void; onGeaendert: () => void }) {
  const { user, hasModule } = useAuthStore()
  const kosten = erfuelltRecht('calculation.cost.view', user?.permissions)
  // Detailblatt N 6.1a (N1): ein PDF-Angebot mit der KI lesen — nur mit `.AI` und Kostenrecht.
  const kiBaustein = hasModule('CALCULATION.AI')
  const [haken, setHaken] = useState<Set<string>>(new Set())
  const [kopfHaken, setKopfHaken] = useState(true)
  const [a, setA] = useState<RequestDetail | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [ziehtDarueber, setZiehtDarueber] = useState(false)
  const dateiEingabe = useRef<HTMLInputElement>(null)

  const laden = useCallback(() => {
    fetchRequest(id)
      .then((d) => {
        setA(d)
        setFehler(null)
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die Anfrage ließ sich nicht laden.')))
  }, [id])
  useEffect(() => laden(), [laden])

  const offen = a ? a.status !== 'CLOSED' && a.status !== 'CANCELLED' : false
  const darf = kosten && offen
  const kiLiest = a?.aiStatus === 'RUNNING'
  useEffect(() => {
    if (!kiLiest) return
    const t = setInterval(laden, 2000)
    return () => clearInterval(t)
  }, [kiLiest, laden])
  // Vorgabe-Häkchen wie in der Vorschlagsliste: nicht bei „niedrig".
  const vorschlaege = a?.lines.filter((l) => l.aiSuggested) ?? []
  const vorschlagSchluessel = vorschlaege.map((l) => l.id).join(',')
  useEffect(() => {
    setHaken(new Set(vorschlaege.filter((l) => l.aiSuggested!.confidence !== 'niedrig').map((l) => l.id)))
    setKopfHaken(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vorschlagSchluessel])

  const kopfStelle = useSpeicherstelle<Datensatz>({
    schluessel: `calcRequest:${id}`,
    bezeichnung: a ? `Anfrage ${a.documentNumber ?? ''} ${a.supplier.name}` : 'Anfrage',
    daten: a as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      const r = await api.patch<{ data: RequestDetail }>(requestUrl(id), patch, mitStand(stand))
      onGeaendert()
      return r.data.data as unknown as Datensatz
    },
    keepalive: { url: requestUrl(id), method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const antwortVom = useFeld<string | null>(kopfStelle, 'answeredAt', { art: 'datum', label: 'Antwort vom', gesperrt: !darf })
  const angebotNr = useFeld<string | null>(kopfStelle, 'supplierOfferNumber', { label: 'Angebotsnummer', gesperrt: !darf })
  const gueltig = useFeld<string | null>(kopfStelle, 'validUntil', { art: 'datum', label: 'gültig bis', gesperrt: !darf })
  const bemerkung = useFeld<string | null>(kopfStelle, 'note', { label: 'Bemerkung', gesperrt: !darf })

  const preiseLesen = async (fid: string) => {
    setFehler(null)
    try {
      const v = await antwortLesen(id, fid, true)
      const geaendert = v.lines.filter((l) => l.changed)
      const text = geaendert.length
        ? `${geaendert.length} Zeile(n) aus der Datei: ${geaendert
            .slice(0, 6)
            .map((l) => `${l.name}: ${l.before == null ? '—' : geldText(l.before)} → ${l.notOffered ? 'n. a.' : l.after == null ? '—' : geldText(l.after)}`)
            .join('; ')}${geaendert.length > 6 ? ' …' : ''}. Übernehmen?`
        : 'Die Datei bringt keine neuen Preise. Trotzdem lesen?'
      if (!(await bestaetige(text, { ok: 'Lesen' }))) return
      await antwortLesen(id, fid, false)
      laden()
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Datei ließ sich nicht lesen.'))
    }
  }

  const kiLesen = async (fid: string, name: string) => {
    if (!(await bestaetige(`„${name}" an den KI-Anbieter des Betriebs senden und lesen? Die Werte kommen als Vorschläge an die Zeilen — erst „Bestätigen" macht sie zur Antwort.`, { ok: 'Mit KI lesen' }))) return
    await aktion(() => anfrageKiLesen(id, fid))
  }

  // 7.2a: Dateien ziehen oder wählen, mehrere auf einmal — auch eine ganze E-Mail (.eml, .msg).
  const ablegen = async (liste: FileList | File[] | null) => {
    const dateien = Array.from(liste ?? [])
    if (!dateien.length) return
    setFehler(null)
    setHinweis(null)
    setLaeuft(true)
    const fehlerTexte: string[] = []
    const hinweise: string[] = []
    try {
      for (const f of dateien) {
        try {
          const d = await uploadRequestFile(id, f)
          setA(d)
          if (d.mailImport) hinweise.push(mailHinweis(d.mailImport))
        } catch (e) {
          fehlerTexte.push(`${f.name}: ${apiErrorText(e, 'Die Datei ließ sich nicht ablegen.')}`)
        }
      }
      onGeaendert()
    } finally {
      setLaeuft(false)
      if (fehlerTexte.length) setFehler(fehlerTexte.join(' · '))
      if (hinweise.length) setHinweis(hinweise.join(' '))
    }
  }

  const aktion = async (was: () => Promise<RequestDetail>) => {
    setLaeuft(true)
    try {
      setA(await was())
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-3xl flex-col border-l border-border shadow-2xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
      <div className="flex items-start justify-between gap-2 border-b border-border p-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Anfrage</div>
          <h2 className="text-lg font-semibold">
            {a?.supplier.name ?? '…'} {a && <span className="text-muted-foreground">· {a.documentNumber ?? 'Entwurf'}</span>}
          </h2>
          {a && (
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
              <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_ANFRAGE[a.status].klasse}`}>{STATUS_ANFRAGE[a.status].label}</span>
              {a.contactName && <span className="text-muted-foreground">{a.contactName}</span>}
              {a.dueAt && <span className="text-muted-foreground">Antwort erbeten bis {tag(a.dueAt)}</span>}
              <Link to={`/correspondence/${a.documentId}`} className="inline-flex items-center gap-0.5 text-primary hover:underline">
                Beleg öffnen <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {a && offen && (
            <button type="button" disabled={laeuft} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted" onClick={() => void aktion(() => closeRequest(id))}>
              <Lock className="h-3.5 w-3.5" /> Abschließen
            </button>
          )}
          {a?.status === 'CLOSED' && (
            <button type="button" disabled={laeuft} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted" onClick={() => void aktion(() => reopenRequest(id))}>
              <RotateCcw className="h-3.5 w-3.5" /> Wieder öffnen
            </button>
          )}
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-auto p-4">
        {fehler && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
        {!kosten && <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">Antworten erfassen braucht das Recht, Kosten zu sehen — die Anfrage ist hier nur lesbar.</p>}
        {a && (
          <>
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <label className="block">
                <span className="mb-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  Antwort vom <FeldStatusZeichen status={antwortVom} />
                </span>
                <input type="date" className={eingabe} {...antwortVom.props} />
              </label>
              <label className="block">
                <span className="mb-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  Angebotsnummer des Lieferanten <FeldStatusZeichen status={angebotNr} />
                </span>
                <input className={eingabe} {...angebotNr.props} />
              </label>
              <label className="block">
                <span className="mb-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  gültig bis <FeldStatusZeichen status={gueltig} />
                </span>
                <input type="date" className={eingabe} {...gueltig.props} />
              </label>
              <label className="block">
                <span className="mb-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  Bemerkung <FeldStatusZeichen status={bemerkung} />
                </span>
                <input className={eingabe} {...bemerkung.props} />
              </label>
            </div>

            <div
              className={`rounded-md border p-3 text-sm transition-colors ${ziehtDarueber ? 'border-dashed border-primary bg-primary/5' : 'border-border'}`}
              onDragOver={(e) => {
                if (!offen || laeuft || !e.dataTransfer.types.includes('Files')) return
                e.preventDefault()
                e.dataTransfer.dropEffect = 'copy'
                setZiehtDarueber(true)
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setZiehtDarueber(false)
              }}
              onDrop={(e) => {
                setZiehtDarueber(false)
                if (!offen || laeuft || !e.dataTransfer.files.length) return
                e.preventDefault()
                void ablegen(e.dataTransfer.files)
              }}
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="font-medium">Angebot des Lieferanten</span>
                {offen && (
                  <>
                    <button type="button" disabled={laeuft} className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50" onClick={() => dateiEingabe.current?.click()}>
                      {laeuft ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Datei hochladen
                    </button>
                    <input
                      ref={dateiEingabe}
                      type="file"
                      multiple
                      className="hidden"
                      accept={a.kind === 'SERVICES' ? '.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.docx,.onlv,.x84,.xml,.eml,.msg' : '.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.docx,.eml,.msg'}
                      onChange={(e) => {
                        const liste = Array.from(e.target.files ?? [])
                        e.target.value = ''
                        void ablegen(liste)
                      }}
                    />
                  </>
                )}
              </div>
              {offen && (
                <p className="mb-1 text-xs text-muted-foreground">
                  {ziehtDarueber ? 'Loslassen zum Ablegen.' : 'Dateien hierher ziehen — auch eine ganze E-Mail aus Outlook (.msg) oder als .eml: Ihr Text wird ein PDF, die Anhänge eigene Dateien.'}
                </p>
              )}
              {hinweis && <p className="mb-1 rounded-md bg-muted px-2 py-1 text-xs">{hinweis}</p>}
              {a.files.length === 0 ? (
                <p className="text-xs text-muted-foreground">Noch keine Datei — die erste wird beim Übernehmen als Preisquelle vermerkt.</p>
              ) : (
                <ul className="space-y-0.5">
                  {a.files.map((f) => (
                    <li key={f.id} className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      <button type="button" className="text-primary hover:underline" onClick={() => void oeffneAngebotsdatei(id, f.id, f.fileName).catch((e) => setFehler(apiErrorText(e, 'Die Datei lässt sich nicht öffnen.')))}>
                        {f.fileName}
                      </button>
                      {/* Detailblatt K 4.3: Antwort eines Nachunternehmers ohne KI lesen — ONLV, X84 oder das ausgefüllte Excel. */}
                      {offen && a.kind === 'SERVICES' && /\.(onlv|x84|xml|xlsx)$/i.test(f.fileName) && (
                        <button type="button" className="text-xs text-primary hover:underline" onClick={() => void preiseLesen(f.id)}>
                          Preise lesen
                        </button>
                      )}
                      {offen && kosten && kiBaustein && f.mimeType === 'application/pdf' && (
                        <button type="button" disabled={kiLiest || laeuft} className="inline-flex items-center gap-0.5 text-xs text-primary hover:underline disabled:opacity-50" onClick={() => void kiLesen(f.id, f.fileName)}>
                          <Sparkles className="h-3 w-3" /> Mit KI lesen
                        </button>
                      )}
                      {offen && (
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-destructive"
                          aria-label={`${f.fileName} entfernen`}
                          onClick={() => void bestaetige(`„${f.fileName}" entfernen?`, { gefaehrlich: true }).then((ja) => {
                              if (ja) void aktion(() => deleteRequestFile(id, f.id))
                            })}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {kiLiest && (
              <div className="rounded-md bg-primary/10 px-3 py-2 text-sm">
                <KiArbeitet text="Die KI liest das Angebot" />
              </div>
            )}
            {a.aiStatus === 'FAILED' && a.aiError && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">Die KI konnte das Angebot nicht lesen: {a.aiError}</div>}
            {(vorschlaege.length > 0 || a.aiSuggested) && (
              <div className="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-1 font-medium">
                    <Sparkles className="h-4 w-4 text-primary" /> Vorschläge aus {a.aiSuggested?.fileName ?? 'dem Angebot'}
                  </span>
                  {darf && (
                    <span className="flex gap-2">
                      <button
                        type="button"
                        disabled={laeuft || (haken.size === 0 && !(kopfHaken && a.aiSuggested))}
                        className="rounded-md bg-primary px-2.5 py-1 text-xs text-primary-foreground disabled:opacity-50"
                        onClick={() => void aktion(() => anfrageKiBestaetigen(id, [...haken], kopfHaken && !!a.aiSuggested))}
                      >
                        Bestätigen ({haken.size + (kopfHaken && a.aiSuggested ? 1 : 0)})
                      </button>
                      <button type="button" disabled={laeuft} className="rounded-md border border-border px-2.5 py-1 text-xs" onClick={() => void aktion(() => anfrageKiVerwerfen(id, vorschlaege.map((l) => l.id), true))}>
                        Alle verwerfen
                      </button>
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Kursiv an den Zeilen: was die KI gelesen hat. Erst „Bestätigen" macht es zur Antwort, erst der Preisspiegel bringt es in die Kalkulation.</p>
                {a.aiSuggested && (a.aiSuggested.supplierOfferNumber || a.aiSuggested.validUntil) && (
                  <label className="flex items-center gap-2 text-xs italic">
                    <input type="checkbox" checked={kopfHaken} disabled={!darf} onChange={(e) => setKopfHaken(e.target.checked)} />
                    Kopf: {a.aiSuggested.supplierOfferNumber ? `Angebot ${a.aiSuggested.supplierOfferNumber}` : ''}
                    {a.aiSuggested.validUntil ? ` · gültig bis ${tag(a.aiSuggested.validUntil)}` : ''}
                  </label>
                )}
              </div>
            )}

            <div>
              <div className="mb-1 text-sm font-medium">Zeilen</div>
              <div className="space-y-2">
                {a.lines.map((l) => (
                  <AntwortZeile
                    key={l.id}
                    anfrageId={id}
                    zeile={l}
                    kgCode={a.kgUnitCode}
                    darf={darf}
                    kosten={kosten}
                    onGeaendert={onGeaendert}
                    vorschlagHaken={haken.has(l.id)}
                    onVorschlagHaken={(an) =>
                      setHaken((h) => {
                        const n = new Set(h)
                        if (an) n.add(l.id)
                        else n.delete(l.id)
                        return n
                      })
                    }
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function AntwortZeile({
  anfrageId,
  zeile,
  kgCode,
  darf,
  kosten,
  onGeaendert,
  vorschlagHaken,
  onVorschlagHaken,
}: {
  anfrageId: string
  zeile: RequestLine
  kgCode: string | null
  darf: boolean
  kosten: boolean
  onGeaendert: () => void
  vorschlagHaken: boolean
  onVorschlagHaken: (an: boolean) => void
}) {
  const [auf, setAuf] = useState(false)
  const url = requestLineUrl(anfrageId, zeile.id)
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `calcRequestLine:${zeile.id}`,
    bezeichnung: zeile.name,
    daten: zeile as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      const r = await api.patch<{ data: RequestLine }>(url, patch, mitStand(stand))
      onGeaendert()
      return r.data.data as unknown as Datensatz
    },
    keepalive: { url, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const d = useStellenDaten(stelle) as unknown as RequestLine
  const nichtAngeboten = useFeld<boolean>(stelle, 'notOffered', { art: 'schalter', label: 'nicht angeboten', gesperrt: !darf })
  const aus = !darf || Boolean(d?.notOffered)
  const ep = useFeld<number | null>(stelle, 'unitPrice', { art: 'zahl', stellen: 4, label: 'EP', gesperrt: aus || !kosten })
  const pe = useFeld<number | null>(stelle, 'priceUnit', {
    art: 'zahl',
    stellen: 0,
    label: 'PE',
    pflicht: true,
    gesperrt: aus || !kosten,
    pruefe: (v) => (v !== null && v >= 1 ? null : 'Die Preiseinheit ist mindestens 1'),
  })
  const rabatt = useFeld<number | null>(stelle, 'discountPercent', { art: 'zahl', stellen: 2, label: 'Rabatt %', gesperrt: aus || !kosten })
  // 7.4a (O6): Der Lieferant bietet je kg an, angefragt war in m — die Antwort trägt ihre eigene Einheit (O-56).
  const lage = zeile.perKg
  const jeWaehlbar = lage.reason !== 'FREE_LINE' && lage.reason !== 'ALREADY_WEIGHT'
  const je = useFeld<string | null>(stelle, 'answerUnitCode', { art: 'auswahl', label: 'je', gesperrt: aus || !kosten || (!lage.possible && !d?.answerUnitCode) })
  const jeKg = Boolean(je.wert)
  const einheitDerAntwort = jeKg ? (kgCode ?? 'kg') : (zeile.unitCode ?? 'Einheit')
  // Kontrolle „= 46,53 je m": Nettopreis je kg × Gewicht je Einheit der Anfrage — nur bei einem gemeinsamen Gewicht.
  const kontrolle = (() => {
    if (!jeKg || ep.wert == null) return null
    if (lage.weightDiffers) return 'Gewicht je Zeile verschieden'
    if (lage.weightPerUnit == null) return null
    const netto = (ep.wert / Math.max(1, pe.wert ?? 1)) * (1 - (rabatt.wert ?? 0) / 100)
    return `= ${geldText(netto * lage.weightPerUnit)} je ${zeile.unitCode ?? 'Einheit'} (${lage.weightPerUnit.toLocaleString('de-DE', { maximumFractionDigits: 4 })} kg je ${zeile.unitCode ?? 'Einheit'})`
  })()
  const lieferzeit = useFeld<number | null>(stelle, 'leadTimeDays', { art: 'zahl', stellen: 0, label: 'Lieferzeit (Tage)', gesperrt: aus })
  const alternative = useFeld<string | null>(stelle, 'alternative', { label: 'Alternative', gesperrt: !darf })
  const notiz = useFeld<string | null>(stelle, 'answerNote', { label: 'Bemerkung', gesperrt: !darf })

  const zelle = `${feldKlasse} w-full text-right tabular-nums`
  return (
    <div className="rounded-md border border-border p-2 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="w-20 text-right tabular-nums">{mengeText(zeile.quantity)}</span>
        <span className="w-10">{zeile.unitCode}</span>
        <span className="flex-1 font-medium">
          {zeile.name}
          {zeile.isFree && <span className="ml-1 rounded bg-muted px-1 text-xs text-muted-foreground">im Beleg dazugeschrieben</span>}
          {zeile.documentQuantityChanged && <span className="ml-1 rounded bg-amber-100 px-1 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Menge im Beleg geändert</span>}
          {zeile.sentFiles.length > 0 && (
            <span className="ml-1 inline-flex items-center text-xs text-muted-foreground" title={zeile.sentFiles.join(', ')}>
              <Paperclip className="h-3 w-3" />
              {zeile.sentFiles.length}
            </span>
          )}
        </span>
        {kosten && d?.netPrice != null && <span className="text-xs text-muted-foreground">netto {geldText(d.netPrice)} je {einheitDerAntwort}</span>}
      </div>
      {zeile.description && <div className="ml-32 whitespace-pre-line text-xs text-muted-foreground">{zeile.description}</div>}
      {zeile.aiSuggested && (
        <KiVorschlagZeile v={zeile.aiSuggested} unitCode={zeile.aiSuggested.answerUnitCode ? (kgCode ?? 'kg') : zeile.unitCode} neu={!!zeile.aiCreated} haken={vorschlagHaken} darf={darf} onHaken={onVorschlagHaken} />
      )}
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-6">
        {kosten && (
          <>
            <label className="block">
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                EP <FeldStatusZeichen status={ep} />
              </span>
              <input className={zelle} inputMode="decimal" {...ep.props} />
            </label>
            <label className="block">
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                je <FeldStatusZeichen status={je} />
              </span>
              {jeWaehlbar ? (
                <select className={`${feldKlasse} w-full`} title={ohneJeKg(lage) ?? undefined} {...je.props}>
                  <option value="">{zeile.unitCode ?? 'Einheit'}</option>
                  {(lage.possible || d?.answerUnitCode) && kgCode && <option value={kgCode}>{kgCode}</option>}
                </select>
              ) : (
                <span className="block py-1 text-sm">{zeile.unitCode ?? '—'}</span>
              )}
            </label>
            <label className="block">
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                PE <FeldStatusZeichen status={pe} />
              </span>
              <input className={zelle} inputMode="numeric" {...pe.props} />
            </label>
            <label className="block">
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                Rabatt % <FeldStatusZeichen status={rabatt} />
              </span>
              <input className={zelle} inputMode="decimal" {...rabatt.props} />
            </label>
          </>
        )}
        <label className="block">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            Lieferzeit (Tage) <FeldStatusZeichen status={lieferzeit} />
          </span>
          <input className={zelle} inputMode="numeric" {...lieferzeit.props} />
        </label>
        <label className={`flex items-end gap-1.5 pb-1 text-xs ${kosten ? '' : 'sm:col-span-2'}`}>
          <input type="checkbox" {...nichtAngeboten.schalter} />
          nicht angeboten <FeldStatusZeichen status={nichtAngeboten} />
        </label>
        <label className="col-span-2 block sm:col-span-3">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            Alternative <FeldStatusZeichen status={alternative} />
          </span>
          <input className={`${feldKlasse} w-full`} placeholder="wenn der Lieferant etwas anderes anbietet" {...alternative.props} />
        </label>
        <label className="col-span-2 block sm:col-span-3">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            Bemerkung <FeldStatusZeichen status={notiz} />
          </span>
          <input className={`${feldKlasse} w-full`} {...notiz.props} />
        </label>
      </div>
      {kosten && kontrolle && <div className="mt-1 text-xs tabular-nums text-muted-foreground">{kontrolle}</div>}
      {kosten && darf && jeWaehlbar && !lage.possible && ohneJeKg(lage) && <div className="mt-1 text-xs text-muted-foreground">{ohneJeKg(lage)}</div>}
      {zeile.links.length > 0 && (
        <div className="mt-1 text-xs">
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setAuf((x) => !x)}>
            {auf ? '▾' : '▸'} steckt in {zeile.links.length} Kostenzeile{zeile.links.length === 1 ? '' : 'n'}
          </button>
          {auf && (
            <ul className="ml-4 mt-0.5 space-y-0.5 text-muted-foreground">
              {zeile.links.map((v) => (
                <li key={v.costLineId}>
                  <Link to={`/calculations/${v.calculationId}?tab=positionen&pos=${v.positionId}`} className="text-primary hover:underline">
                    {v.calculationLabel}
                  </Link>{' '}
                  {v.calculationName} · {v.positionLabel} · {mengeText(v.quantity)} {zeile.unitCode}
                  {v.applied && <span className="ml-1 rounded bg-primary/15 px-1 text-primary">übernommen</span>}
                  {!v.isCurrent && <span className="ml-1">(ältere Fassung)</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

/** N1 (6.1a): der Vorschlag der KI an einer Antwortzeile — kursiv, mit Häkchen, Fundstelle und Sicherheit. */
function KiVorschlagZeile({ v, unitCode, neu, haken, darf, onHaken }: { v: KiAntwortVorschlag; unitCode: string | null; neu: boolean; haken: boolean; darf: boolean; onHaken: (an: boolean) => void }) {
  const teile = v.notOffered
    ? ['nicht angeboten']
    : [
        v.unitPrice !== null ? `EP ${geldText(v.unitPrice)} je ${v.priceUnit > 1 ? `${v.priceUnit.toLocaleString('de-DE')} ` : ''}${unitCode ?? 'Einheit'}`.trim() : 'ohne Preis',
        v.discountPercent ? `${v.discountPercent.toLocaleString('de-DE')} % Rabatt` : '',
        v.leadTimeDays !== null ? `${v.leadTimeDays} Tage` : '',
      ].filter(Boolean)
  if (v.alternative) teile.push(`Alternative: ${v.alternative}`)
  // O-62: Gewichtseinheit im Angebot, aber „je kg" ging nicht — der Preis gilt so nicht je Einheit der Anfrage.
  if (v.unitHint) teile.push(`Einheit im Angebot: ${v.unitHint}`)
  return (
    <label className="mt-1 flex items-start gap-2 rounded bg-primary/5 px-2 py-1 text-xs italic">
      <input type="checkbox" className="mt-0.5" checked={haken} disabled={!darf} onChange={(e) => onHaken(e.target.checked)} />
      <span className="flex-1">
        {neu && <span className="mr-1 not-italic text-muted-foreground">neue Zeile ·</span>}
        {teile.join(' · ')}
        <span className="text-muted-foreground">
          {' '}
          — „{v.offerText}"{v.page ? `, Seite ${v.page}` : ''}
          {v.quoteFound === false ? ' (Zitat nicht gefunden)' : ''}
        </span>
      </span>
      <span className={`rounded px-1.5 not-italic ${SICHER_KLASSE[v.confidence]}`}>{v.confidence}</span>
    </label>
  )
}
